import React, { useCallback, useState } from 'react';

import type { WrappedComponentProps } from 'react-intl-next';

import { toolbarMessages } from '@atlaskit/editor-common/messages';
import { DropdownMenuWithKeyboardNavigation as DropdownMenu } from '@atlaskit/editor-common/ui-menu';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorMenuZIndex } from '@atlaskit/editor-shared-styles';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { DropdownToolbarButton } from './dropdown-toolbar-button';
import { useMenuState } from './hooks/menu-state';
import { MoreButton } from './more-button';
import { type MenuIconItem, type ToolbarType } from './types';

type DropdownMenuProps = {
	editorView: EditorView;
	hasFormattingActive: boolean;
	hasMoreButton: boolean;
	isDisabled?: boolean;
	isReducedSpacing: boolean;
	items: {
		items: MenuIconItem[];
	}[];
	moreButtonLabel: string;
	popupsBoundariesElement?: HTMLElement;
	popupsMountPoint?: HTMLElement;
	popupsScrollableElement?: HTMLElement;
	toolbarType: ToolbarType;
} & WrappedComponentProps;

export const FormattingTextDropdownMenu: React.MemoExoticComponent<
	({
		editorView,
		moreButtonLabel,
		isReducedSpacing,
		items,
		hasFormattingActive,
		popupsBoundariesElement,
		popupsMountPoint,
		popupsScrollableElement,
		hasMoreButton,
		intl,
		toolbarType,
		isDisabled,
	}: DropdownMenuProps) => React.JSX.Element
> = React.memo(
	({
		editorView,
		moreButtonLabel,
		isReducedSpacing,
		items,
		hasFormattingActive,
		popupsBoundariesElement,
		popupsMountPoint,
		popupsScrollableElement,
		hasMoreButton,
		intl,
		toolbarType,
		isDisabled,
	}: DropdownMenuProps): React.JSX.Element => {
		const [isMenuOpen, toggleMenu, closeMenu] = useMenuState();
		const [isOpenedByKeyboard, setIsOpenedByKeyboard] = useState(false);
		const onItemActivated = useCallback(
			({ item, shouldCloseMenu = true }: { item: MenuIconItem; shouldCloseMenu: boolean }) => {
				if (item) {
					item.command(editorView.state, editorView.dispatch);
					if (shouldCloseMenu) {
						closeMenu();
					}
				}
			},
			[editorView.state, editorView.dispatch, closeMenu],
		);

		const activeItem = items[0].items.find((item) => item.isActive);
		const defaultIcon = editorExperiment('platform_editor_controls', 'variant1')
			? items[0].items[0]?.elemBefore
			: undefined;
		const defaultIconName = items[0].items[0]?.value.name;
		const iconBefore = activeItem ? activeItem?.elemBefore : defaultIcon;
		const activeIconName = activeItem?.value.name || defaultIconName;

		return (
			<DropdownMenu
				mountTo={popupsMountPoint}
				onOpenChange={closeMenu}
				boundariesElement={popupsBoundariesElement}
				scrollableElement={popupsScrollableElement}
				onItemActivated={onItemActivated}
				isOpen={isMenuOpen}
				items={items}
				zIndex={akEditorMenuZIndex}
				fitHeight={188}
				fitWidth={editorExperiment('platform_editor_controls', 'control') ? 136 : 230}
				shouldUseDefaultRole
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) 
				section={{ hasSeparator: true }}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) 
				shouldFocusFirstItem={() => {
					if (isOpenedByKeyboard) {
						setIsOpenedByKeyboard(false);
					}
					return isOpenedByKeyboard;
				}}
			>
				{hasMoreButton ? (
					<MoreButton
						isSelected={isMenuOpen || hasFormattingActive}
						label={moreButtonLabel}
						isReducedSpacing={isReducedSpacing}
						isDisabled={false}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) 
						onClick={() => {
							toggleMenu();
							setIsOpenedByKeyboard(false);
						}}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) 
						onKeyDown={(event: React.KeyboardEvent) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								toggleMenu();
								setIsOpenedByKeyboard(true);
							}
						}}
						aria-expanded={isMenuOpen}
					/>
				) : (
					<DropdownToolbarButton
						isReducedSpacing={isReducedSpacing}
						isDisabled={Boolean(isDisabled)}
						isSelected={isMenuOpen}
						label={intl.formatMessage(toolbarMessages.textFormat)}
						aria-expanded={isMenuOpen}
						aria-haspopup
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) 
						onClick={() => {
							toggleMenu();
							setIsOpenedByKeyboard(false);
						}}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) 
						onKeyDown={(event: React.KeyboardEvent) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								toggleMenu();
								setIsOpenedByKeyboard(true);
							}
						}}
						toolbarType={toolbarType}
						iconBefore={iconBefore}
						activeIconName={activeIconName}
					/>
				)}
			</DropdownMenu>
		);
	},
);
